import { Counter } from './types';
import { Reducer, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';

type State<T extends Counter> = Record<string, T>;

type Action<T extends Counter> =
	| { type: 'increase' | 'decrease'; payload: { passengerId: string } }
	| { type: 'set'; payload: { passengerId: string; count: number } }
	| { type: 'bulkEnsureAtLeastOne'; payload: { passengerIds: string[] } }
	| { type: 'reset'; payload: State<T> }
	| { type: 'clear' };

const buildStateFromCounters = <T extends Counter>(counters: T[]): State<T> => {
	return counters.reduce<State<T>>((state, counter) => ({ ...state, [counter.passenger.id]: counter }), {});
};

const getCounterHash = (counters: Counter[]): string => {
	return counters.map(c => `${c.passenger.id}:${c.count}`).join('/');
};

const reducer = <T extends Counter>(state: State<T>, action: Action<T>): State<T> => {
	switch (action.type) {
		case 'increase':
		case 'decrease': {
			const existing = state[action.payload.passengerId];
			if (!existing) {
				return state;
			}

			return {
				...state,
				[action.payload.passengerId]: {
					...existing,
					count: action.type === 'increase' ? existing.count + 1 : existing.count - 1
				}
			};
		}

		case 'set': {
			const existing = state[action.payload.passengerId];
			if (!existing) {
				return state;
			}

			return {
				...state,
				[action.payload.passengerId]: { ...existing, count: action.payload.count }
			};
		}

		case 'bulkEnsureAtLeastOne': {
			const patches: Partial<State<T>> = {};

			for (const passengerId of action.payload.passengerIds) {
				const existing = state[passengerId];

				if (!existing || existing.count > 0) {
					continue;
				}

				patches[passengerId] = { ...existing, count: existing.count + 1 };
			}

			return Object.keys(patches).length > 0 ? { ...state, ...patches } : state;
		}

		case 'reset':
			return action.payload;

		case 'clear':
			return Object.entries(state).reduce<State<T>>(
				(acc, [id, counter]) => ({ ...acc, [id]: { ...counter, count: 0 } }),
				{}
			);

		default:
			return state;
	}
};

export const useCounters = <T extends Counter>(initialCounters: T[]) => {
	const [state, dispatch] = useReducer(
		reducer as Reducer<State<T>, Action<T>>,
		initialCounters,
		buildStateFromCounters
	);

	const isInitialMount = useRef(true);

	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;

			return;
		}

		dispatch({ type: 'reset', payload: buildStateFromCounters(initialCounters) });
	}, [initialCounters]);

	const onIncrease = useCallback(
		(passengerId: string) => {
			dispatch({ type: 'increase', payload: { passengerId } });
		},
		[dispatch]
	);

	const onDecrease = useCallback(
		(passengerId: string) => {
			dispatch({ type: 'decrease', payload: { passengerId } });
		},
		[dispatch]
	);

	const onReset = useCallback(
		(counters: T[]) => {
			dispatch({ type: 'reset', payload: buildStateFromCounters(counters) });
		},
		[dispatch]
	);

	const onClear = useCallback(() => {
		dispatch({ type: 'clear' });
	}, [dispatch]);

	const onAddForAllPassengers = useCallback(
		(passengerIds: string[]) => {
			dispatch({ type: 'bulkEnsureAtLeastOne', payload: { passengerIds } });
		},
		[dispatch]
	);

	const counters = useMemo(() => Object.values(state), [state]);

	const isChanged = useMemo(() => getCounterHash(initialCounters) !== getCounterHash(counters), [
		initialCounters,
		counters
	]);

	return {
		// data
		counters,
		isChanged,

		// methods
		onDecrease,
		onIncrease,
		onReset,
		onClear,
		onAddForAllPassengers
	};
};
